import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import Aura from '@primeng/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { appRoutes } from './app.routes';
import { baseUrlInterceptor } from './components/shared/data-access/interceptors/base-url.interceptor';
import { environment } from './environments/environment';
import { API_BASE_URL } from './components/shared/models/api-base-url.token';
import { AuthService } from './components/auth/data-access/auth.service';
import { authInterceptor } from './components/shared/data-access/interceptors/auth.interceptor';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ThemePreferenceService } from './app/layout/service/theme-preference.service';
import { ThemeApplyService } from './app/layout/service/theme-apply.service';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

function initAppFactory(
  auth: AuthService,
  theme: ThemePreferenceService,
  themeApply: ThemeApplyService,
) {
  return () => {
    theme.applyCache(); // instant device theme (no flash, safe pre-login)
    theme.startWatching(); // persist future theme changes
    auth.initializeSession();
    // Cold-load of a public/auth page → force the Clubside brand palette so a
    // saved primary color never bleeds outside the authenticated app.
    if (themeApply.isPublicPath(window.location.pathname)) {
      themeApply.applyDefault();
    }
  };
}

export const appConfig: ApplicationConfig = {
    providers: [
      DialogService,
      MessageService,
      provideZonelessChangeDetection(),
      provideRouter(
        appRoutes,
        withInMemoryScrolling({
          anchorScrolling: 'enabled',
          scrollPositionRestoration: 'enabled'
        })
        // Avoid blocking initial nav if it’s prompting the Zone warning in your setup.
        // withEnabledBlockingInitialNavigation()
      ),
      // Zoneless-safe animations. app.menuitem uses `@angular/animations` triggers
      // ([@children]), so an animations provider is required. The EAGER module
      // (BrowserAnimationsModule / provideAnimations) suppresses the zoneless CD
      // scheduler (async data hangs until you interact) AND mis-times PrimeNG's
      // overlay-mask DOM cleanup, so a closed drawer/dialog leaves an invisible,
      // click-blocking mask. The async provider gives the same animation support
      // without either problem.
      provideAnimationsAsync(),
      provideHttpClient(withInterceptors([authInterceptor, baseUrlInterceptor])),
      { provide: API_BASE_URL, useValue: environment.apiBaseUrl },
      { provide: APP_INITIALIZER, useFactory: initAppFactory, deps: [AuthService, ThemePreferenceService, ThemeApplyService], multi: true },
      providePrimeNG({
        theme: { 
          preset: Aura, 
          options: { 
            darkModeSelector: '.app-dark' 
          }
        }
      }),
      
    ]
};
